import {
  FILTER_TYPE,
  API_INFINITY_VALUE,
  filters,
} from '../../../../../constants';
import { dots } from '../../../../../functions';
import type { CustomCompanyFilter } from './types';

export const generateFuturePerformanceFilters = (): CustomCompanyFilter[] => [
  {
    id: filters.futureOneYearGrowth,
    label: '1 Year Earnings Growth Estimate',
    labels: {
      ...dots(60, { start: -30, step: 15 }),
      '-35': '-∞',
      '-30': '-30%',
      0: '0%',
      30: '30%',
      35: '∞',
    },
    defaultValue: `["${filters.futureOneYearGrowth}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.futureOneYearGrowth}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -35;
        } else if (value >= API_INFINITY_VALUE) {
          return 35;
        } else {
          return value;
        }
      }),
    min: -35,
    max: 35,
    type: FILTER_TYPE.RANGE,
    softMin: -30,
    softMax: 30,
    valueResolver: (range) =>
      range.map((value) =>
        value < -30
          ? API_INFINITY_VALUE * -1
          : value > 30
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  {
    id: filters.futureThreeYearGrowth,
    label: '3 Year Earnings Growth Estimate',
    labels: {
      ...dots(60, { start: -30, step: 15 }),
      '-35': '-∞',
      '-30': '-30%',
      0: '0%',
      30: '30%',
      35: '∞',
    },
    defaultValue: `["${filters.futureThreeYearGrowth}", "between", -${API_INFINITY_VALUE}, ${API_INFINITY_VALUE}]`,
    rule: (range) =>
      `["${filters.futureThreeYearGrowth}", "between", ${range[0]}, ${range[1]}]`,
    ruleToValue: ([_, __, ...range]) =>
      range.map(Number).map((value) => {
        if (value <= API_INFINITY_VALUE * -1) {
          return -35;
        } else if (value >= API_INFINITY_VALUE) {
          return 35;
        } else {
          return value;
        }
      }),
    min: -35,
    max: 35,
    type: FILTER_TYPE.RANGE,
    softMin: -30,
    softMax: 30,
    valueResolver: (range) =>
      range.map((value) =>
        value < -30
          ? API_INFINITY_VALUE * -1
          : value > 30
            ? API_INFINITY_VALUE
            : value
      ),
    titleFormatter: ([_, __, ...range]) => {
      const [min, max] = range.map(Number);
      if (min === max) {
        return `is ${min}`;
      } else if (min <= API_INFINITY_VALUE * -1 && max !== API_INFINITY_VALUE) {
        return `is between -∞ and ${max}%`;
      } else if (min !== API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between ${min}% and ∞`;
      } else if (min <= API_INFINITY_VALUE * -1 && max >= API_INFINITY_VALUE) {
        return `is between -∞ and ∞`;
      } else {
        return `is between ${min}% and ${max}%`;
      }
    },
  },
  {
    defaultValue: `["${filters.futureThreeYearRoe}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.futureThreeYearRoe}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    ruleToValue: ([_, __, min, max]) => [min, max],
    id: filters.futureThreeYearRoe,
    label: '3 Year ROE Estimate',
    subLabel: '(Return on Equity)',
    labels: { ...dots(50, { step: 5 }), 0: '0%', 50: '50%', 55: '∞' },
    min: 0,
    max: 55,
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 50 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
];
